import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import ReactMarkdown from 'react-markdown'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { ImageLoader } from 'browser/components/atomic-elements/atoms/image-loader/image-loader'
import { UI2PlatformContext } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'

import {
  FileViewerProps,
  THUMBNAIL_SIZE_PRESETS,
  ThumbnailSizePreset,
} from 'shared-libs/components/ui2/fields/field-ui/ui2-file-viewer/ui2-file-viewer-props.ts'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_file-viewer.scss'

const DEFAULT_PLACEHOLDER = 'No image available.'

export interface IUI2FileViewerProps extends FileViewerProps, Omit<IBaseProps, 'children'> {}

interface IUI2FileViewerState {
  carouselIndex: number
  lightboxIndex: number | null
}

export class UI2FileViewer extends React.PureComponent<IUI2FileViewerProps, IUI2FileViewerState> {
  static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  public static defaultProps: Partial<IUI2FileViewerProps> = {
    thumbnailSize: 'md',
    thumbnailFit: 'contain',
    showFileName: false,
  }

  public state: IUI2FileViewerState = { carouselIndex: 0, lightboxIndex: null }

  public componentDidUpdate(prevProps: IUI2FileViewerProps) {
    if (prevProps.value !== this.props.value || prevProps.uri !== this.props.uri) {
      this.setState({ carouselIndex: 0 })
    }
  }

  public render() {
    const { className, style, isDisabled } = this.props
    const entries = this.getEntries()
    return (
      <>
        <div
          className={classNames('ui2-fileViewer', { 'ui2-fileViewer--disabled': isDisabled }, className)}
          style={style}
        >
          {_.isEmpty(entries) ? this.renderEmptyState() : this.renderViewer(entries)}
        </div>
        {this.renderLightbox()}
      </>
    )
  }

  private renderEmptyState(): React.ReactNode {
    const { placeholder } = this.props
    const message = placeholder || DEFAULT_PLACEHOLDER
    const height = this.resolveThumbnailHeight()
    return (
      <div className='ui2-fileViewer-emptyState' style={{ minHeight: height }}>
        <ReactMarkdown>{message}</ReactMarkdown>
      </div>
    )
  }

  private renderViewer(entries: any[]): React.ReactNode {
    const { showFileName, thumbnailFit } = this.props
    const total = entries.length
    const carouselIndex = Math.min(this.state.carouselIndex, total - 1)
    const current = entries[carouselIndex]
    const height = this.resolveThumbnailHeight()
    const fitClass = `ui2-fileViewer-thumbnailWrap--${thumbnailFit}`
    const isNoPreview = !!current._noPreview
    return (
      <div className={classNames('ui2-fileViewer-thumbnailWrap', fitClass)} style={{ height }}>
        {isNoPreview ? this.renderNoPreviewSlide(current) : (
          <ImageLoader
            src={current.uri}
            className='ui2-fileViewer-image'
            onClick={() => this.handleOpenLightbox(carouselIndex)}
          />
        )}
        {total > 1 && (
          <>
            {carouselIndex > 0 && (
              <button
                className='ui2-fileViewer-nav ui2-fileViewer-nav--left'
                type='button'
                onClick={this.handlePrev}
                aria-label='Previous file'
              >
                <UI2Icon name={UI2IconNames.CHEVRON_LEFT} size={20} />
              </button>
            )}
            {carouselIndex < total - 1 && (
              <button
                className='ui2-fileViewer-nav ui2-fileViewer-nav--right'
                type='button'
                onClick={this.handleNext}
                aria-label='Next file'
              >
                <UI2Icon name={UI2IconNames.CHEVRON_RIGHT} size={20} />
              </button>
            )}
            <div className='ui2-fileViewer-indicator'>
              {carouselIndex + 1} of {total}
            </div>
          </>
        )}
        {showFileName && current.name && (
          <div className='ui2-fileViewer-fileNameBadge' title={current.name}>
            {current.name}
          </div>
        )}
        {this.renderActionBar(current, 'overlay', isNoPreview)}
      </div>
    )
  }

  private renderNoPreviewSlide(file: any): React.ReactNode {
    const name = file.name || file._pdfSourceName || 'Unknown file'
    return (
      <div className='ui2-fileViewer-noPreview'>
        <UI2Icon name={UI2IconNames.FILE_TEXT} size={32} />
        <span className='ui2-fileViewer-noPreview-name'>{name}</span>
        <span className='ui2-fileViewer-noPreview-hint'>No preview available</span>
      </div>
    )
  }

  // Download/print/zoom buttons. `overlay` variant floats on the inline thumbnail;
  // `lightbox` variant sits in the lightbox toolbar (larger icons, inverted colors).
  // `isNoPreview` is passed through to the caller but does not suppress the zoom button —
  // non-previewable files can still open the lightbox to access download/print actions.
  private renderActionBar(current: any, variant: 'overlay' | 'lightbox', isNoPreview = false): React.ReactNode {
    const { showDownloadButton, showPrintButton, onRemoveCurrent, isDisabled } = this.props
    // All interactions are blocked when the field is disabled.
    if (isDisabled) return null
    const includeZoom = variant === 'overlay'
    if (!includeZoom && !showDownloadButton && !showPrintButton && !onRemoveCurrent) return null
    const isMobileWeb = this.context === 'mobileWeb'
    const iconSize = variant === 'overlay' ? 16 : isMobileWeb ? 24 : 18
    const barClass = classNames('ui2-fileViewer-actionBar', `ui2-fileViewer-actionBar--${variant}`)
    return (
      <div className={barClass}>
        {showDownloadButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handleDownload(current) }}
            aria-label='Download'
            title='Download'
          >
            <UI2Icon name={UI2IconNames.DOWNLOAD} size={iconSize} />
          </button>
        )}
        {showPrintButton && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handlePrint(current) }}
            aria-label='Print'
            title='Print'
          >
            <UI2Icon name={UI2IconNames.PRINTER} size={iconSize} />
          </button>
        )}
        {includeZoom && (
          <button
            className='ui2-fileViewer-actionBarButton'
            type='button'
            onClick={(e) => { e.stopPropagation(); this.handleOpenLightbox(this.state.carouselIndex) }}
            aria-label={isNoPreview ? 'Open file details' : 'Open lightbox'}
            title={isNoPreview ? 'Open file details' : 'Open lightbox'}
          >
            <UI2Icon name={isNoPreview ? UI2IconNames.EXTERNAL_LINK : UI2IconNames.ZOOM_IN} size={iconSize} />
          </button>
        )}
        {onRemoveCurrent && (
          <button
            className='ui2-fileViewer-actionBarButton ui2-fileViewer-actionBarButton--danger'
            type='button'
            onClick={(e) => { e.stopPropagation(); onRemoveCurrent(this.state.carouselIndex) }}
            aria-label='Remove file'
            title='Remove file'
          >
            <UI2Icon name={UI2IconNames.TRASH} size={iconSize} />
          </button>
        )}
      </div>
    )
  }

  private renderLightbox(): React.ReactNode {
    const { lightboxIndex } = this.state
    if (lightboxIndex === null || typeof document === 'undefined') return null
    const entries = this.getEntries()
    if (_.isEmpty(entries)) return null
    const total = entries.length
    const index = Math.min(lightboxIndex, total - 1)
    const current = entries[index]
    const isMobileWeb = this.context === 'mobileWeb'
    return ReactDOM.createPortal(
      <UI2FileViewerLightbox
        images={entries}
        index={index}
        current={current}
        total={total}
        isMobileWeb={isMobileWeb}
        onPrev={this.handleLightboxPrev}
        onNext={this.handleLightboxNext}
        onClose={this.handleCloseLightbox}
        actions={this.renderActionBar(current, 'lightbox', !!current._noPreview)}
      />,
      document.body,
    )
  }

  private handlePrev = () => {
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.max(0, carouselIndex - 1) }))
  }

  private handleNext = () => {
    const total = this.getEntries().length
    this.setState(({ carouselIndex }) => ({ carouselIndex: Math.min(total - 1, carouselIndex + 1) }))
  }

  // Opens the lightbox at the given carousel index. Works for all entries,
  // including non-previewable ones (the lightbox shows a placeholder + actions).
  private handleOpenLightbox = (carouselIndex: number) => {
    if (this.props.isDisabled) return
    this.setState({ lightboxIndex: carouselIndex })
  }

  private handleCloseLightbox = () => {
    this.setState({ lightboxIndex: null })
  }

  private handleLightboxPrev = () => {
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.max(0, lightboxIndex - 1),
    }))
  }

  private handleLightboxNext = () => {
    const total = this.getEntries().length
    this.setState(({ lightboxIndex }) => ({
      lightboxIndex: lightboxIndex === null ? null : Math.min(total - 1, lightboxIndex + 1),
    }))
  }

  private handleDownload = (current: any) => downloadFileEntry(current)

  private handlePrint = (current: any) => {
    printFileEntry(current, this.props.entity, this.props.printOptions)
  }

  // All carousel entries — images, previewable PDFs, and non-previewable files.
  // Non-previewable entries carry `_noPreview: true` so renderViewer can show
  // a placeholder slot rather than a broken image.
  private getEntries(): any[] {
    const { uri, value } = this.props
    if (_.isString(uri) && !_.isEmpty(uri)) return [{ uri, type: 'image' }]
    if (_.isArray(uri)) {
      return uri
        .filter((entry) => _.isString(entry) && !_.isEmpty(entry))
        .map((u: string) => ({ uri: u, type: 'image' }))
    }
    if (_.isString(value) && !_.isEmpty(value)) return [{ uri: value, type: 'image' }]
    if (_.isArray(value)) {
      return value
        .map((entry: any) => {
          if (_.isString(entry) && !_.isEmpty(entry)) return { uri: entry, type: 'image' }
          if (entry && _.isString(entry.uri) && !_.isEmpty(entry.uri)) {
            return { ...entry, type: entry.type || 'image' }
          }
          return null
        })
        .filter(Boolean) as any[]
    }
    const entries: any[] = []
    const files = _.get(value, 'files', [])
    _.forEach(files, (file: any) => {
      if (file.type === 'image') {
        entries.push(file)
      } else if (file.type === 'pdf') {
        const tagged = (file.preview || []).map((page: any) => ({
          ...page,
          _pdfSourceUri: file.uri,
          _pdfSourceName: file.name,
        }))
        if (tagged.length > 0) {
          Array.prototype.push.apply(entries, tagged)
        } else {
          entries.push({ ...file, _noPreview: true })
        }
      } else {
        entries.push({ ...file, _noPreview: true })
      }
    })
    return entries
  }

  // Subset used by the lightbox — entries that have a real image URI.
  private getPreviewableEntries(): any[] {
    return this.getEntries().filter((e: any) => !e._noPreview)
  }

  private resolveThumbnailHeight(): number {
    const { thumbnailSize } = this.props
    if (typeof thumbnailSize === 'number') return thumbnailSize
    return THUMBNAIL_SIZE_PRESETS[thumbnailSize as ThumbnailSizePreset] ?? THUMBNAIL_SIZE_PRESETS.md
  }
}

function extractFileName(uri: string): string | undefined {
  try {
    const pathname = new URL(uri, window.location.href).pathname
    const last = pathname.split('/').filter(Boolean).pop()
    return last ? decodeURIComponent(last) : undefined
  } catch {
    return undefined
  }
}

function resolveFileViewerPrintUrl(current: any, entity?: any, printOptions?: any): string | undefined {
  if (entity && printOptions?.printablePath) {
    const aggregateUri = _.get(entity, [...printOptions.printablePath.split('.'), 'aggregate', 'uri'])
    if (aggregateUri) return aggregateUri
  }
  // For PDF preview pages, prefer the original document so all pages print.
  return current?._pdfSourceUri || current?.uri
}

function isPdfFileViewerUrl(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf')
  } catch {
    return url.toLowerCase().includes('.pdf')
  }
}

// Exported so sibling components (e.g. UI2FileUploadField chip lightbox) can
// reuse the same download/print behavior without duplicating the implementation.
export async function downloadFileEntry(current: any): Promise<void> {
  // When the current item is a preview page of a larger document (PDF), download
  // the original document instead of the single rendered page image.
  const uri = current?._pdfSourceUri || current?.uri
  if (!uri) return
  const fileName = current._pdfSourceName || current.name || extractFileName(uri) || 'download'
  try {
    const res = await fetch(uri, { mode: 'cors' })
    if (!res.ok) throw new Error('fetch failed')
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
  } catch {
    window.open(uri, '_blank', 'noopener,noreferrer')
  }
}

export function printFileEntry(current: any, entity?: any, printOptions?: any): void {
  const url = resolveFileViewerPrintUrl(current, entity, printOptions)
  if (!url) return

  if (isPdfFileViewerUrl(url)) {
    // Chrome's PDF viewer runs in a cross-origin plugin frame, so
    // iframe.contentWindow.print() is always blocked — even with a blob URL.
    // Solution: open a blank popup (must be synchronous, before any async work,
    // so popup blockers allow it), embed the PDF blob inside it, and let the
    // popup's own inline script call window.print() with no cross-origin access.
    const popup = window.open('', '_blank')
    const fallback = () => {
      if (popup && !popup.closed) popup.location.href = url
      else window.open(url, '_blank', 'noopener,noreferrer')
    }
    fetch(url, { mode: 'cors' })
      .then((res) => { if (!res.ok) throw new Error('fetch failed'); return res.blob() })
      .then((blob) => {
        if (!popup || popup.closed) { return }
        const blobUrl = URL.createObjectURL(blob)
        popup.document.write(`<!doctype html><html>
          <head><title>Print</title>
          <style>html,body,embed{margin:0;padding:0;width:100%;height:100%}</style>
          </head><body>
          <embed src="${blobUrl}" type="application/pdf">
          <script>setTimeout(function(){window.print()},1500)<\/script>
          </body></html>`)
        popup.document.close()
        // Revoke after a long delay so the PDF stays accessible during printing.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120000)
      })
      .catch(fallback)
    return
  }

  // Non-PDF: write the image into a hidden iframe and trigger print from there.
  // This path has no PDF plugin, so contentWindow.print() works fine.
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', visibility: 'hidden' })
  const triggerPrint = () => {
    try {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } finally {
      setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe) }, 1000)
    }
  }
  document.body.appendChild(iframe)
  const doc = iframe.contentDocument
  if (!doc) { document.body.removeChild(iframe); return }
  doc.open()
  doc.write(`<!doctype html><html><head><title>Print</title>
    <style>@page{margin:12mm}html,body{margin:0;padding:0}img{display:block;max-width:100%;height:auto}</style>
    </head><body><img src="${url}" /></body></html>`)
  doc.close()
  const img = doc.querySelector('img')
  if (img && !img.complete) {
    img.addEventListener('load', triggerPrint)
    img.addEventListener('error', triggerPrint)
  } else {
    triggerPrint()
  }
}

// ─── Lightbox ────────────────────────────────────────────────────────────────
// Exported so siblings (e.g. UI2FileUploadField chip list) can reuse the same
// portal-rendered lightbox UI when their own affordances are tapped.

interface IUI2FileViewerLightboxProps {
  images: any[]
  index: number
  current: any
  total: number
  isMobileWeb?: boolean
  /** Names the image on screen — e.g. the guided-capture slot it satisfied. */
  caption?: string
  onPrev: () => void
  onNext: () => void
  onClose: () => void
  actions?: React.ReactNode
}

interface IUI2FileViewerLightboxState {
  isZoomed: boolean
}

export class UI2FileViewerLightbox extends React.PureComponent<IUI2FileViewerLightboxProps, IUI2FileViewerLightboxState> {
  public state: IUI2FileViewerLightboxState = { isZoomed: false }

  public componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    document.body.style.overflow = 'hidden'
  }

  public componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.body.style.overflow = ''
  }

  public componentDidUpdate(prevProps: IUI2FileViewerLightboxProps) {
    if (prevProps.current !== this.props.current) {
      this.setState({ isZoomed: false })
    }
  }

  public render() {
    const { current, total, index, isMobileWeb } = this.props
    const { isZoomed } = this.state
    const isNoPreview = !!current?._noPreview
    return (
      <div
        className={classNames('ui2-fileViewer-lightbox', { 'ui2-fileViewer-lightbox--mobileWeb': isMobileWeb })}
        role='dialog'
        aria-modal='true'
      >
        <div className='ui2-fileViewer-lightbox-backdrop' onClick={this.props.onClose} />
        <div className='ui2-fileViewer-lightbox-content'>
          {isNoPreview ? (
            <div className='ui2-fileViewer-lightbox-noPreview'>
              <UI2Icon name={UI2IconNames.FILE_TEXT} size={48} />
              <span className='ui2-fileViewer-lightbox-noPreview-name'>{current.name || current._pdfSourceName || 'Unknown file'}</span>
              <span className='ui2-fileViewer-lightbox-noPreview-hint'>No preview available</span>
            </div>
          ) : (
            <img
              className={classNames('ui2-fileViewer-lightbox-image', {
                'ui2-fileViewer-lightbox-image--zoomed': isZoomed,
              })}
              src={current?.uri}
              alt={current?.name || ''}
              onClick={this.toggleZoom}
            />
          )}
        </div>
        {this.props.caption && (
          <div className='ui2-fileViewer-lightbox-caption'>{this.props.caption}</div>
        )}
        <div className='ui2-fileViewer-lightbox-toolbar'>
          {this.props.actions}
          <button
            className='ui2-fileViewer-lightbox-close'
            type='button'
            onClick={this.props.onClose}
            aria-label='Close'
          >
            <UI2Icon name={UI2IconNames.X} size={20} />
          </button>
        </div>
        {total > 1 && (
          <>
            {index > 0 && (
              <button
                className='ui2-fileViewer-lightbox-nav ui2-fileViewer-lightbox-nav--left'
                type='button'
                onClick={this.props.onPrev}
                aria-label='Previous'
              >
                <UI2Icon name={UI2IconNames.CHEVRON_LEFT} size={28} />
              </button>
            )}
            {index < total - 1 && (
              <button
                className='ui2-fileViewer-lightbox-nav ui2-fileViewer-lightbox-nav--right'
                type='button'
                onClick={this.props.onNext}
                aria-label='Next'
              >
                <UI2Icon name={UI2IconNames.CHEVRON_RIGHT} size={28} />
              </button>
            )}
            <div className='ui2-fileViewer-lightbox-indicator'>
              {index + 1} of {total}
            </div>
          </>
        )}
      </div>
    )
  }

  private toggleZoom = () => {
    this.setState((prev) => ({ isZoomed: !prev.isZoomed }))
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); this.props.onClose() }
    else if (e.key === 'ArrowLeft') this.props.onPrev()
    else if (e.key === 'ArrowRight') this.props.onNext()
  }
}
